'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';

export function PricingSection() {
  return (
    <section
      id="pricing"
      className="snap-start relative py-24 bg-canvas border-t border-border-subtle overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 z-0 opacity-40 mix-blend-multiply"
        style={{
          backgroundImage: `url(/backgrounds/pricing-bg.webp)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-12">
        <div className="text-center mb-16">
          <h2
            className="text-[32px] sm:text-[40px] font-light text-ink leading-[1.1] tracking-[-0.02em] mb-4"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Simple, transparent pricing
          </h2>
          <p
            className="text-[16px] text-text-secondary"
            style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 300 }}
          >
            Choose a plan for your agent workload.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-[1000px] mx-auto">
          {/* Dev Tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-card/80 backdrop-blur-md border border-border-subtle rounded-none p-8 flex flex-col shadow-card-lg"
          >
            <div className="mb-8">
              <h3
                className="text-[18px] font-medium text-ink mb-2"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                Developer
              </h3>
              <div className="flex items-baseline gap-1">
                <span className="text-[32px] font-light text-ink">$10</span>
                <span className="text-body text-text-tertiary">/mo</span>
              </div>
              <p className="text-body-sm text-text-secondary mt-4">
                Perfect for side projects and testing.
              </p>
            </div>

            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start gap-3 text-body text-text-secondary">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                10,000 traces / month
              </li>
              <li className="flex items-start gap-3 text-body text-text-secondary">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Basic evaluations
              </li>
              <li className="flex items-start gap-3 text-body text-text-secondary">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                7-day data retention
              </li>
            </ul>

            <Link
              href="/signup"
              className="w-full py-3 px-4 border border-border-subtle text-ink text-body font-medium rounded-none text-center hover:bg-cream-50 transition-colors"
            >
              Sign up
            </Link>
          </motion.div>

          {/* Startup Tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white dark:bg-[#0E0E11]/95 backdrop-blur-md border border-border-subtle rounded-none p-8 flex flex-col relative overflow-hidden shadow-2xl scale-105 z-10"
          >
            <div className="absolute top-0 right-0 bg-warm-brown text-white text-[10px] font-bold uppercase tracking-wider py-1 px-3 rounded-none">
              Most Popular
            </div>
            <div className="mb-8">
              <h3
                className="text-[18px] font-medium text-ink mb-2"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                Startup
              </h3>
              <div className="flex items-baseline gap-1">
                <span className="text-[32px] font-light text-ink">$450</span>
                <span className="text-body text-ink/60">/mo</span>
              </div>
              <p className="text-body-sm text-ink/80 mt-4">For production AI applications.</p>
            </div>

            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start gap-3 text-body text-ink/90">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                1,000,000 traces / month
              </li>
              <li className="flex items-start gap-3 text-body text-ink/90">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Advanced evaluations
              </li>
              <li className="flex items-start gap-3 text-body text-ink/90">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Automated issue grouping
              </li>
              <li className="flex items-start gap-3 text-body text-ink/90">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                30-day data retention
              </li>
            </ul>

            <Link
              href="/signup"
              className="w-full py-3 px-4 bg-warm-brown text-white text-body font-medium rounded-none text-center hover:bg-warm-brown-hover transition-colors"
            >
              Sign up
            </Link>
          </motion.div>

          {/* Enterprise Tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-card/80 backdrop-blur-md border border-border-subtle rounded-none p-8 flex flex-col shadow-card-lg"
          >
            <div className="mb-8">
              <h3
                className="text-[18px] font-medium text-ink mb-2"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                Enterprise
              </h3>
              <div className="flex items-baseline gap-1">
                <span className="text-[32px] font-light text-ink">Custom</span>
              </div>
              <p className="text-body-sm text-text-secondary mt-4">For large scale deployments.</p>
            </div>

            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start gap-3 text-body text-text-secondary">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Unlimited traces
              </li>
              <li className="flex items-start gap-3 text-body text-text-secondary">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Custom data retention
              </li>
              <li className="flex items-start gap-3 text-body text-text-secondary">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Dedicated support channel
              </li>
              <li className="flex items-start gap-3 text-body text-text-secondary">
                <svg
                  className="w-5 h-5 text-gold flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                VPC deployment options
              </li>
            </ul>

            <Link
              href="https://cal.com/neel-sharma/sentrial?overlayCalendar=true"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 px-4 border border-border-subtle text-ink text-body font-medium rounded-none text-center hover:bg-cream-50 transition-colors"
            >
              Contact Sales
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
